import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import styles from './ScheduleMeeting.module.css';
import { API } from '../../config';
import { FaCalendarPlus, FaSpinner, FaLink, FaEdit, FaChevronDown, FaRedo } from 'react-icons/fa';
import Sidebar from './Sidebar';

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const DAY_FULL = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

// Given a date range + selected weekday indices, returns all matching dates
function getRecurringDates(startDate, endDate, selectedDayIndices) {
    const dates = [];
    const cursor = new Date(startDate);
    cursor.setHours(0, 0, 0, 0);
    const end = new Date(endDate);
    end.setHours(23, 59, 59, 999);
    while (cursor <= end) {
        if (selectedDayIndices.includes(cursor.getDay())) {
            dates.push(new Date(cursor));
        }
        cursor.setDate(cursor.getDate() + 1);
    }
    return dates;
}

const ScheduleMeeting = () => {
    const { state } = useLocation();
    const navigate = useNavigate();
    const isEditMode = !!state?.meetingToEdit;

    // --- Shared state ---
    const [allStudents, setAllStudents] = useState([]);
    const [selectedParticipants, setSelectedParticipants] = useState([]);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState({ type: '', text: '' });
    const [meetingLink, setMeetingLink] = useState('');

    // --- Mode toggle (only available when NOT editing) ---
    const [isRecurring, setIsRecurring] = useState(false);

    // --- Single meeting state ---
    const [meetingData, setMeetingData] = useState({
        meetingKey: '',
        topic: '',
        startsAt: '',
        agenda: '',
        duration: '01:00',
        presenterId: '',
    });

    // --- Recurring meeting state ---
    const [recurringData, setRecurringData] = useState({
        topic: '',
        agenda: '',
        duration: '01:00',
        presenterId: '',
        startDate: '',   // date range start
        endDate: '',     // date range end
        time: '07:00',   // time of day (HH:mm)
    });
    const [selectedDays, setSelectedDays] = useState([]); // array of 0-6
    const [recurringPreview, setRecurringPreview] = useState([]); // computed dates

    // Recompute preview whenever range/days change
    useEffect(() => {
        if (recurringData.startDate && recurringData.endDate && selectedDays.length > 0) {
            const dates = getRecurringDates(recurringData.startDate, recurringData.endDate, selectedDays);
            setRecurringPreview(dates);
        } else {
            setRecurringPreview([]);
        }
    }, [recurringData.startDate, recurringData.endDate, selectedDays]);

    // Fetch students
    useEffect(() => {
        const fetchStudents = async () => {
            try {
                const response = await fetch(`${API.GET_STUDENTS_ENDPOINT}`);
                const result = await response.json();
                if (result.success) setAllStudents(result.students);
            } catch (err) {
                console.error('Error fetching students:', err);
            }
        };
        fetchStudents();
    }, []);

    // Pre-fill for edit mode
    useEffect(() => {
        if (isEditMode && state.meetingToEdit) {
            const meeting = state.meetingToEdit;
            const dateObj = new Date(meeting.startTime.replace(' IST', ''));
            const pad = (n) => String(n).padStart(2, '0');
            const formattedStartsAt = `${dateObj.getFullYear()}-${pad(dateObj.getMonth() + 1)}-${pad(dateObj.getDate())}T${pad(dateObj.getHours())}:${pad(dateObj.getMinutes())}`;
            const totalMinutes = Math.floor(parseInt(meeting.duration) / 60000);
            const formattedDuration = `${pad(Math.floor(totalMinutes / 60))}:${pad(totalMinutes % 60)}`;

            setMeetingData({
                meetingKey: meeting.meetingKey,
                topic: meeting.topic,
                agenda: meeting.agenda,
                presenterId: meeting.presenter,
                startsAt: formattedStartsAt,
                duration: formattedDuration,
            });

            const initialParticipants = meeting.participants ? meeting.participants.map(p => p.email) : [];
            if (meeting.presenterEmail && !initialParticipants.includes(meeting.presenterEmail)) {
                initialParticipants.push(meeting.presenterEmail);
            }
            setSelectedParticipants(initialParticipants);
        }
    }, [isEditMode, state]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setMeetingData(prev => ({ ...prev, [name]: value }));
    };

    const handleRecurringChange = (e) => {
        const { name, value } = e.target;
        setRecurringData(prev => ({ ...prev, [name]: value }));
    };

    const toggleDay = (dayIndex) => {
        setSelectedDays(prev =>
            prev.includes(dayIndex) ? prev.filter(d => d !== dayIndex) : [...prev, dayIndex]
        );
    };

    const handleParticipantChange = (email) => {
        setSelectedParticipants(prev =>
            prev.includes(email) ? prev.filter(e => e !== email) : [...prev, email]
        );
    };

    const filteredStudents = allStudents.filter(s =>
        s.FullName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        s.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        s.EmailId?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const buildPayload = (topic, agenda, presenterId, duration, startTimeStr) => {
        const [hours, minutes] = duration.split(':');
        const durationMs = (parseInt(hours) * 60 + parseInt(minutes)) * 60 * 1000;
        const participantList = selectedParticipants.map(email => ({ email: email.trim() })).filter(p => p.email);
        return {
            session: {
                topic,
                agenda,
                presenter: presenterId,
                participants: participantList,
                startTime: new Date(startTimeStr).toLocaleString('en-US', {
                    month: 'short', day: '2-digit', year: 'numeric',
                    hour: '2-digit', minute: '2-digit', hour12: true,
                }),
                duration: durationMs,
                timezone: 'Asia/Calcutta',
            },
        };
    };

    // ── Single meeting submit ──────────────────────────────────────────────────
    const handleSingleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setMessage({ type: '', text: '' });
        setMeetingLink('');

        if (!meetingData.topic || !meetingData.startsAt || !meetingData.presenterId) {
            setMessage({ type: 'error', text: 'Please fill out all required fields.' });
            setLoading(false);
            return;
        }
        if (new Date(meetingData.startsAt) < new Date()) {
            setMessage({ type: 'error', text: 'The meeting time must be in the future.' });
            setLoading(false);
            return;
        }

        try {
            const payload = buildPayload(
                meetingData.topic, meetingData.agenda,
                meetingData.presenterId, meetingData.duration, meetingData.startsAt
            );
            const endpoint = isEditMode
                ? `${API.UPDATE_ZOHO_MEETING_ENDPOINT}?meetingKey=${meetingData.meetingKey}`
                : API.CREATE_ZOHO_MEETING_ENDPOINT;
            const method = isEditMode ? 'PUT' : 'POST';

            const response = await fetch(endpoint, {
                method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
            });
            const result = await response.json();

            if (result.success) {
                setMessage({ type: 'success', text: isEditMode ? 'Meeting updated!' : 'Meeting scheduled!' });
                setMeetingLink(result.joinLink || '');
                navigate('/getmeeting');
            } else {
                setMessage({ type: 'error', text: result.message || 'Failed.' });
            }
        } catch (err) {
            setMessage({ type: 'error', text: `Error: ${err.message}` });
        } finally {
            setLoading(false);
        }
    };

    // ── Recurring meeting submit ───────────────────────────────────────────────
    const handleRecurringSubmit = async (e) => {
        e.preventDefault();
        setMessage({ type: '', text: '' });

        if (!recurringData.topic || !recurringData.presenterId || !recurringData.startDate || !recurringData.endDate || selectedDays.length === 0) {
            setMessage({ type: 'error', text: 'Please fill all fields and select at least one day.' });
            return;
        }
        if (new Date(recurringData.endDate) < new Date(recurringData.startDate)) {
            setMessage({ type: 'error', text: 'End date must be after start date.' });
            return;
        }
        if (recurringPreview.length === 0) {
            setMessage({ type: 'error', text: 'No matching dates found for the selected range and days.' });
            return;
        }

        setLoading(true);
        let successCount = 0;
        let failCount = 0;

        for (const date of recurringPreview) {
            const [h, m] = recurringData.time.split(':');
            date.setHours(parseInt(h), parseInt(m), 0, 0);
            const isoString = date.toISOString();

            try {
                const payload = buildPayload(
                    recurringData.topic, recurringData.agenda,
                    recurringData.presenterId, recurringData.duration, isoString
                );
                const response = await fetch(API.CREATE_ZOHO_MEETING_ENDPOINT, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(payload),
                });
                const result = await response.json();
                if (result.success) successCount++;
                else failCount++;
            } catch {
                failCount++;
            }
        }

        setLoading(false);
        if (failCount === 0) {
            setMessage({ type: 'success', text: `✅ ${successCount} meetings scheduled successfully!` });
            setTimeout(() => navigate('/getmeeting'), 1500);
        } else {
            setMessage({ type: 'error', text: `${successCount} scheduled, ${failCount} failed. Check logs.` });
        }
    };

    // ── Participants dropdown (shared) ─────────────────────────────────────────
    const ParticipantsDropdown = () => (
        <div className={styles.formGroup}>
            <label>Participants</label>
            <div className={styles.multiSelectDropdown}>
                <div className={styles.selectedItems} onClick={() => setIsDropdownOpen(o => !o)}>
                    {selectedParticipants.length > 0 ? (
                        selectedParticipants.map(email => {
                            const s = allStudents.find(st => st.EmailId === email);
                            return (
                                <span key={email} className={styles.selectedTag}>
                                    {s ? `${s.FullName} (${s.name})` : email}
                                </span>
                            );
                        })
                    ) : <span>Select participants...</span>}
                    <FaChevronDown className={isDropdownOpen ? styles.arrowUp : ''} />
                </div>
                <AnimatePresence>
                    {isDropdownOpen && (
                        <motion.div
                            className={styles.dropdownContent}
                            initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.2 }}
                        >
                            <input
                                type="text" className={styles.searchBar}
                                placeholder="Search students..."
                                value={searchQuery}
                                onChange={e => setSearchQuery(e.target.value)}
                            />
                            {filteredStudents.length > 0 ? filteredStudents.map(student => (
                                <div key={student.EmailId} className={styles.dropdownItemMeet}>
                                    <div className={styles.inpcheck}>
                                        <input type="checkbox"
                                            checked={selectedParticipants.includes(student.EmailId)}
                                            onChange={() => handleParticipantChange(student.EmailId)}
                                        />
                                    </div>
                                    <label>{student.FullName} ({student.name})</label>
                                </div>
                            )) : <div className={styles.dropdownItem}>No students found.</div>}
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </div>
    );

    return (
        <div className={styles.withSidebar}>
            <Sidebar />
            <div className={styles.container}>
                <motion.div
                    className={styles.formCard}
                    initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                >
                    <h2 className={styles.title}>
                        {isEditMode ? <FaEdit /> : isRecurring ? <FaRedo /> : <FaCalendarPlus />}
                        {isEditMode ? 'Edit Meeting' : isRecurring ? 'Schedule Recurring Meetings' : 'Schedule a New Meeting'}
                    </h2>

                    {/* Mode toggle — only show when not editing */}
                    {!isEditMode && (
                        <div style={toggleWrap}>
                            <button
                                type="button"
                                style={{ ...toggleBtn, ...(isRecurring ? {} : toggleBtnActive) }}
                                onClick={() => setIsRecurring(false)}
                            >
                                Single
                            </button>
                            <button
                                type="button"
                                style={{ ...toggleBtn, ...(isRecurring ? toggleBtnActive : {}) }}
                                onClick={() => setIsRecurring(true)}
                            >
                                Recurring
                            </button>
                        </div>
                    )}

                    {/* ── SINGLE FORM ── */}
                    {!isRecurring && (
                        <form onSubmit={handleSingleSubmit} className={styles.form}>
                            <div className={styles.formGroup}>
                                <label>Topic</label>
                                <input type="text" name="topic" value={meetingData.topic} onChange={handleChange} required />
                            </div>
                            <div className={styles.formGroup}>
                                <label>Presenter</label>
                                <select name="presenterId" value={meetingData.presenterId} onChange={handleChange} required>
                                    <option value="">Select Presenter</option>
                                    <option value="664033335">Bspark Admin</option>
                                </select>
                            </div>
                            <ParticipantsDropdown />
                            <div className={styles.formGroup}>
                                <label>Date & Time</label>
                                <input type="datetime-local" name="startsAt" value={meetingData.startsAt} onChange={handleChange} required />
                            </div>
                            <div className={styles.formGroup}>
                                <label>Duration (HH:mm)</label>
                                <input type="text" name="duration" value={meetingData.duration} onChange={handleChange} pattern="[0-9]{2}:[0-9]{2}" title="Format: HH:mm" />
                            </div>
                            <div className={styles.formGroup}>
                                <label>Agenda</label>
                                <textarea name="agenda" value={meetingData.agenda} onChange={handleChange} rows="4" />
                            </div>
                            <button type="submit" className={styles.submitBtn} disabled={loading}>
                                {loading ? <FaSpinner className={styles.spinner} /> : (isEditMode ? 'Update Meeting' : 'Schedule Meeting')}
                            </button>
                        </form>
                    )}

                    {/* ── RECURRING FORM ── */}
                    {isRecurring && (
                        <form onSubmit={handleRecurringSubmit} className={styles.form}>
                            <div className={styles.formGroup}>
                                <label>Topic</label>
                                <input type="text" name="topic" value={recurringData.topic} onChange={handleRecurringChange} required />
                            </div>
                            <div className={styles.formGroup}>
                                <label>Presenter</label>
                                <select name="presenterId" value={recurringData.presenterId} onChange={handleRecurringChange} required>
                                    <option value="">Select Presenter</option>
                                    <option value="664033335">Bspark Admin</option>
                                </select>
                            </div>
                            <ParticipantsDropdown />

                            {/* Day-of-week picker */}
                            <div className={styles.formGroup}>
                                <label>Repeat on Days</label>
                                <div style={dayPickerWrap}>
                                    {DAYS.map((day, i) => (
                                        <button
                                            key={day} type="button"
                                            style={{
                                                ...dayBtn,
                                                ...(selectedDays.includes(i) ? dayBtnActive : {})
                                            }}
                                            onClick={() => toggleDay(i)}
                                        >
                                            {day}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Date range */}
                            <div style={{ display: 'flex', gap: '1rem' }}>
                                <div className={styles.formGroup} style={{ flex: 1 }}>
                                    <label>Start Date</label>
                                    <input type="date" name="startDate" value={recurringData.startDate} onChange={handleRecurringChange} required />
                                </div>
                                <div className={styles.formGroup} style={{ flex: 1 }}>
                                    <label>End Date</label>
                                    <input type="date" name="endDate" value={recurringData.endDate} onChange={handleRecurringChange} required />
                                </div>
                            </div>

                            {/* Time of day */}
                            <div className={styles.formGroup}>
                                <label>Time of Day</label>
                                <input type="time" name="time" value={recurringData.time} onChange={handleRecurringChange} required />
                            </div>

                            <div className={styles.formGroup}>
                                <label>Duration (HH:mm)</label>
                                <input type="text" name="duration" value={recurringData.duration} onChange={handleRecurringChange} pattern="[0-9]{2}:[0-9]{2}" title="Format: HH:mm" />
                            </div>
                            <div className={styles.formGroup}>
                                <label>Agenda</label>
                                <textarea name="agenda" value={recurringData.agenda} onChange={handleRecurringChange} rows="3" />
                            </div>

                            {/* Preview box */}
                            {recurringPreview.length > 0 && (
                                <motion.div
                                    style={previewBox}
                                    initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                                >
                                    <p style={previewTitle}>
                                        📅 {recurringPreview.length} meeting{recurringPreview.length > 1 ? 's' : ''} will be created:
                                    </p>
                                    <div style={previewList}>
                                        {recurringPreview.map((d, i) => (
                                            <span key={i} style={previewChip}>
                                                {DAY_FULL[d.getDay()]}, {d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })} @ {recurringData.time}
                                            </span>
                                        ))}
                                    </div>
                                </motion.div>
                            )}

                            <button type="submit" className={styles.submitBtn} disabled={loading || recurringPreview.length === 0}>
                                {loading
                                    ? <><FaSpinner className={styles.spinner} /> Creating meetings...</>
                                    : `Schedule ${recurringPreview.length > 0 ? recurringPreview.length : ''} Meeting${recurringPreview.length !== 1 ? 's' : ''}`
                                }
                            </button>
                        </form>
                    )}

                    {/* Messages */}
                    <AnimatePresence>
                        {message.text && (
                            <motion.div
                                className={`${styles.message} ${styles[message.type]}`}
                                initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -10 }}
                            >
                                {message.text}
                            </motion.div>
                        )}
                    </AnimatePresence>

                    {meetingLink && (
                        <motion.div className={styles.linkCard} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
                            <p className={styles.linkText}>Meeting created!</p>
                            <a href={meetingLink} target="_blank" rel="noopener noreferrer" className={styles.linkBtn}>
                                <FaLink /> Join Meeting (Host)
                            </a>
                        </motion.div>
                    )}
                </motion.div>
            </div>
        </div>
    );
};

// ── Inline styles for new elements ────────────────────────────────────────────
const toggleWrap = {
    display: 'flex', gap: '0.5rem', marginBottom: '1.5rem',
    background: '#f0f0f0', borderRadius: '8px', padding: '4px',
};
const toggleBtn = {
    flex: 1, padding: '0.5rem 1rem', border: 'none', borderRadius: '6px',
    cursor: 'pointer', fontWeight: '500', fontSize: '0.95rem',
    background: 'transparent', color: '#666', transition: 'all 0.2s',
};
const toggleBtnActive = {
    background: '#6a0dad', color: '#fff',
    boxShadow: '0 2px 6px rgba(106,13,173,0.3)',
};
const dayPickerWrap = {
    display: 'flex', gap: '0.4rem', flexWrap: 'wrap', marginTop: '0.25rem',
};
const dayBtn = {
    width: '42px', height: '42px', borderRadius: '50%', border: '2px solid #e2e8f0',
    background: '#fff', color: '#4a5568', fontWeight: '600', fontSize: '0.75rem',
    cursor: 'pointer', transition: 'all 0.2s',
};
const dayBtnActive = {
    background: '#6a0dad', color: '#fff', borderColor: '#6a0dad',
};
const previewBox = {
    background: '#f8f0ff', border: '1px solid #d6b4f8', borderRadius: '10px',
    padding: '1rem',
};
const previewTitle = {
    fontWeight: '600', color: '#6a0dad', marginBottom: '0.75rem', fontSize: '0.95rem',
};
const previewList = {
    display: 'flex', flexWrap: 'wrap', gap: '0.4rem',
};
const previewChip = {
    background: '#fff', border: '1px solid #c084fc', borderRadius: '20px',
    padding: '3px 10px', fontSize: '0.8rem', color: '#6a0dad',
};

export default ScheduleMeeting;